import { ChangeEvent, useState } from "react";
import { faUndo } from "@fortawesome/free-solid-svg-icons/faUndo";
import { DCUIContent, DCUIContentCapturePage, DCUITheme } from "@iddataweb/idw-common-ui/types";
import { BOOLEAN_RADIO_INPUT_OPTIONS } from "@constants/boolean-radio-input-options";
import { resources } from "@constants/hardcoded-strings";
import { DCUICaptureStepTypes } from "@enums/dcui-capture-step-types";
import { DCUIThemingTabTypes } from "@enums/dcui-theming-tab-types";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { PreviewEntityTypes } from "@enums/preview-entity-types";
import { DCUIStepResources } from "@idw-types/common/dcui/dcui-step-resources";
import { DCUIThemingCaptureStepValidationErrors } from "@idw-types/common/validation/common/dcui-theming-validation-errors";
import { DCUIThemingStep } from "@idw-types/common/dcui/dcui-theming-step";
import { getCaptureStepScreenOrientationMediaUrl } from "@utils/dcui-capture-step-util";
import { isStringTrue } from "@iddataweb/idw-common-ui/utils";
import { ContentPageBody } from "../../../../common/content-page-body/content-page-body";
import { ContentPageHeader } from "../../../../common/content-page-header/content-page-header";
import { ThemingFormContainer } from "../../../../common/theming-form-container/theming-form-container";
import { ThemingFormSection } from "../../../../common/theming-form-section/theming-form-section";
import { ThemingConfigurationContainer } from "../../../../common/theming-configuration-container/theming-configuration-container";
import { ThemingSubHeaderContainer } from "../../../../common/theming-sub-header-container/theming-sub-header-container";
import { DCUIThemingPreviewContainer } from "../../../../common/dcui-theming-preview-container/dcui-theming-preview-container";
import { InputFormGroup } from "../../../../common/input-form-group/input-form-group";
import { TextareaFormGroup } from "../../../../common/textarea-form-group/textarea-form-group";
import { RadioInputFormGroup } from "../../../../common/radio-input-form-group/radio-input-form-group";
import { ThemingFormGroupWrapper } from "@containers/common/theming-input-form-group/theming-form-group-wrapper";
import { PageLevelTypes } from "@enums/page-level-types";
import { FileInputFormGroup } from "@containers/common/file-input-form-group/file-input-form-group";
import { ResourceCategoryTypes } from "@enums/resource-category-types";

type DCUIThemingStepTabContainerProps = {
    isDisabled: boolean;
    validationErrors: DCUIThemingCaptureStepValidationErrors
    dcuiContent: DCUIContent;
    inheritedDCUIContent: DCUIContent;
    pageLevel: PageLevelTypes;
    dcuiTheme: DCUITheme;
    tabType: DCUIThemingTabTypes;
    stepType: DCUICaptureStepTypes;
    stepResources: DCUIStepResources;
    documentCaptureSteps: Array<DCUIThemingStep>;
    previewEntityType: PreviewEntityTypes;
    updateDCUIContentPropertyValue: (dcuiContent: DCUIContent) => void;
    uploadFileToS3: (file: File, category: string) => Promise<string>;
};

export const DCUIThemingStepTabContainer = (props: DCUIThemingStepTabContainerProps) => {
    const {
        isDisabled,
        validationErrors,
        dcuiContent,
        inheritedDCUIContent,
        pageLevel,
        dcuiTheme,
        tabType,
        stepType,
        stepResources,
        documentCaptureSteps,
        previewEntityType,
        updateDCUIContentPropertyValue,
        uploadFileToS3
    } = props;

    const [labelWidth] = useState(LabelWidthTypes.EXTRA_SMALL);
    const [layout] = useState(LayoutTypes.COLUMN);

    const stepContentField = stepType as keyof DCUIContent;
    const stepContent = dcuiContent[stepContentField] as DCUIContentCapturePage;
    const defaultStepContent = inheritedDCUIContent[stepContentField] as DCUIContentCapturePage;
    const [customMediaUploadInProgress, setCustomMediaUploadInProgress] = useState(false);

    const updateDCUIContentStepContentFieldValue = (key: keyof DCUIContentCapturePage, value: string | boolean) => {
        const newPropertyValue: DCUIContent = {
            ...dcuiContent,
            [stepContentField]: {
                ...stepContent,
                [key]: value
            }
        };
        updateDCUIContentPropertyValue(newPropertyValue);
    };

    const handleTitleChange = (event: ChangeEvent<HTMLInputElement>) => {
        updateDCUIContentStepContentFieldValue("label", event.target.value);
    };

    const handleResetTitle = () => {
        updateDCUIContentStepContentFieldValue("label", defaultStepContent.label);
    };

    const handleInstructionsChange = (event: ChangeEvent<HTMLInputElement>) => {
        updateDCUIContentStepContentFieldValue("instructionsHtml", event.target.value);
    };

    const handleResetInstructions = () => {
        updateDCUIContentStepContentFieldValue("instructionsHtml", defaultStepContent.instructionsHtml);
    };

    const handleShowScreenOrientationPreviewChange = (newValue: string) => {
        updateDCUIContentStepContentFieldValue("showScreenOrientationPreview", isStringTrue(newValue));
    };

    const handleResetShowScreenOrientationPreview = () => {
        updateDCUIContentStepContentFieldValue("showScreenOrientationPreview", defaultStepContent.showScreenOrientationPreview);
    };

    const handleScreenOrientationPreviewTextChange = (event: ChangeEvent<HTMLInputElement>) => {
        updateDCUIContentStepContentFieldValue("screenOrientationPreviewText", event.target.value);
    };

    const handleResetScreenOrientationPreviewText = () => {
        updateDCUIContentStepContentFieldValue("screenOrientationPreviewText", defaultStepContent.screenOrientationPreviewText);
    };

    const handleUseCustomVideoOrImageChange = (newValue: string) => {
        updateDCUIContentStepContentFieldValue("useCustomScreenOrientationVideo", isStringTrue(newValue));
    };

    const handleResetUseCustomVideoOrImage = () => {
        updateDCUIContentStepContentFieldValue("useCustomScreenOrientationVideo", defaultStepContent.useCustomScreenOrientationVideo);
    };

    const handleCustomMediaUrlChange = async (event: ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files.length > 0) {
            const file = files[0];

            setCustomMediaUploadInProgress(true);
            const uploadResult = await uploadFileToS3(file, `${stepType}-${ResourceCategoryTypes.DCUI_CUSTOM_MEDIAS}`);
            if (uploadResult) {
                updateDCUIContentStepContentFieldValue("screenOrientationMediaUrl", uploadResult);
            }
            setCustomMediaUploadInProgress(false);
        }
       
    };

    const handleResetCustomMediaUrl = () => {
        const mediaUrl = getCaptureStepScreenOrientationMediaUrl(stepType, inheritedDCUIContent);
        updateDCUIContentStepContentFieldValue("screenOrientationMediaUrl", mediaUrl);
    };

    const handleCaptureButtonLabelChange = (event: ChangeEvent<HTMLInputElement>) => {
        updateDCUIContentStepContentFieldValue("nativeCameraButtonText", event.target.value);
    };

    const handleResetCaptureButtonLabel = () => {
        updateDCUIContentStepContentFieldValue("nativeCameraButtonText", defaultStepContent.nativeCameraButtonText);
    };

    return (
        <div>
            <ContentPageHeader title={stepResources.title} />
            <ContentPageBody isMaxWidth={true}>
                <ThemingSubHeaderContainer description={stepResources.description} />
                <ThemingConfigurationContainer>
                    <ThemingFormContainer>
                        <ThemingFormSection>

                            <ThemingFormGroupWrapper
                                inheritedThemingPropertyValue={defaultStepContent.label}
                                currentThemingPropertyValue={stepContent.label}
                                pageLevel={pageLevel}
                            >
                                <InputFormGroup
                                    controlId={`dcui-theming-step-${stepType}-title-input`}
                                    automationId={`$dcui-theming-step-${stepType}-title-input`}
                                    disabled={isDisabled}
                                    isRequired={true}
                                    label={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.common.inputs.title.label}
                                    ariaLabel={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.common.inputs.title.label}
                                    labelWidth={labelWidth}
                                    layout={layout}
                                    onChange={handleTitleChange}
                                    value={stepContent.label}
                                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.common.inputs.title.tooltip(stepResources.title)}
                                    afterLabelIcon={isDisabled ? null : faUndo}
                                    afterLabelIconOnClick={handleResetTitle}
                                />
                            </ThemingFormGroupWrapper>
                            {validationErrors.titleError}

                            <ThemingFormGroupWrapper
                                inheritedThemingPropertyValue={defaultStepContent.instructionsHtml}
                                currentThemingPropertyValue={stepContent.instructionsHtml}
                                pageLevel={pageLevel}
                            >
                                <TextareaFormGroup
                                    controlId={`dcui-theming-step-${stepType}-instructions-input`}
                                    automationId={`$dcui-theming-step-${stepType}-instructions-input`}
                                    disabled={isDisabled}
                                    isRequired={true}
                                    label={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.common.inputs.instructions.label}
                                    ariaLabel={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.common.inputs.instructions.label}
                                    labelWidth={labelWidth}
                                    layout={layout}
                                    onChange={handleInstructionsChange}
                                    value={stepContent.instructionsHtml}
                                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.common.inputs.instructions.tooltip(stepResources.title)}
                                    afterLabelIcon={isDisabled ? null : faUndo}
                                    afterLabelIconOnClick={handleResetInstructions}
                                />
                            </ThemingFormGroupWrapper>
                            {validationErrors.instructionsError}

                            <ThemingFormGroupWrapper
                                inheritedThemingPropertyValue={defaultStepContent.showScreenOrientationPreview}
                                currentThemingPropertyValue={stepContent.showScreenOrientationPreview}
                                pageLevel={pageLevel}
                            >
                                <RadioInputFormGroup
                                    controlId={`dcui-theming-step-${stepType}-screen-orientation-radio-input`}
                                    automationId={`dcui-theming-step-${stepType}-screen-orientation-radio-input`}
                                    label={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.common.inputs.showScreenOrientationPreview.label}
                                    layout={layout}
                                    labelWidth={labelWidth}
                                    onChange={handleShowScreenOrientationPreviewChange}
                                    options={BOOLEAN_RADIO_INPUT_OPTIONS}
                                    value={stepContent.showScreenOrientationPreview ? "true" : "false"}
                                    disabled={isDisabled}
                                    isRequired={true}
                                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.common.inputs.showScreenOrientationPreview.tooltip}
                                    afterLabelIcon={isDisabled ? null : faUndo}
                                    afterLabelIconOnClick={handleResetShowScreenOrientationPreview}
                                />
                            </ThemingFormGroupWrapper>
                            {stepContent.showScreenOrientationPreview && (
                                <>
                                    <ThemingFormGroupWrapper
                                        inheritedThemingPropertyValue={defaultStepContent.screenOrientationPreviewText}
                                        currentThemingPropertyValue={stepContent.screenOrientationPreviewText}
                                        pageLevel={pageLevel}
                                    >
                                        <InputFormGroup
                                            controlId={`dcui-theming-step-${stepType}-screen-orientation-input`}
                                            automationId={`$dcui-theming-step-${stepType}-screen-orientation-input`}
                                            disabled={isDisabled}
                                            isRequired={true}
                                            label={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.common.inputs.screenOrientationPreviewText.label}
                                            ariaLabel={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.common.inputs.screenOrientationPreviewText.label}
                                            labelWidth={labelWidth}
                                            layout={layout}
                                            onChange={handleScreenOrientationPreviewTextChange}
                                            value={stepContent.screenOrientationPreviewText}
                                            afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.common.inputs.screenOrientationPreviewText.tooltip}
                                            afterLabelIcon={isDisabled ? null : faUndo}
                                            afterLabelIconOnClick={handleResetScreenOrientationPreviewText}
                                        />
                                    </ThemingFormGroupWrapper>
                                    {validationErrors.screenOrientationPreviewTextError}
                                </>
                            )}

                            <ThemingFormGroupWrapper
                                inheritedThemingPropertyValue={defaultStepContent.useCustomScreenOrientationVideo}
                                currentThemingPropertyValue={stepContent.useCustomScreenOrientationVideo}
                                pageLevel={pageLevel}
                            >
                                <RadioInputFormGroup
                                    controlId={`dcui-theming-step-${stepType}-use-custom-video-radio-input`}
                                    automationId={`dcui-theming-step-${stepType}-use-custom-video-radio-input`}
                                    label={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.common.inputs.useCustomVideoOrImage.label}
                                    layout={layout}
                                    labelWidth={labelWidth}
                                    onChange={handleUseCustomVideoOrImageChange}
                                    options={BOOLEAN_RADIO_INPUT_OPTIONS}
                                    value={stepContent.useCustomScreenOrientationVideo ? "true" : "false"}
                                    disabled={isDisabled}
                                    isRequired={true}
                                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.common.inputs.useCustomVideoOrImage.tooltip}
                                    afterLabelIcon={isDisabled ? null : faUndo}
                                    afterLabelIconOnClick={handleResetUseCustomVideoOrImage}
                                />
                            </ThemingFormGroupWrapper>
                            {stepContent.useCustomScreenOrientationVideo && (
                                <>
                                    <ThemingFormGroupWrapper
                                        inheritedThemingPropertyValue={defaultStepContent.screenOrientationMediaUrl}
                                        currentThemingPropertyValue={stepContent.screenOrientationMediaUrl}
                                        pageLevel={pageLevel}
                                    >
                                        <FileInputFormGroup
                                            controlId={`dcui-theming-step-${stepType}-custom-media-url-input`}
                                            automationId={`$dcui-theming-step-${stepType}-custom-media-url-input`}
                                            disabled={isDisabled}
                                            isRequired={true}
                                            label={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.common.inputs.customMediaUrl.label}
                                            ariaLabel={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.common.inputs.customMediaUrl.label}
                                            labelWidth={labelWidth}
                                            layout={layout}
                                            onChange={handleCustomMediaUrlChange}
                                            value={stepContent.screenOrientationMediaUrl}
                                            accept={"image/png, image/gif, image/jpeg, image/jpg, video/mp4"}
                                            uploadInProgress={customMediaUploadInProgress}
                                            afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.common.inputs.customMediaUrl.tooltip(stepResources.title)}
                                            afterLabelIcon={isDisabled ? null : faUndo}
                                            afterLabelIconOnClick={handleResetCustomMediaUrl}
                                        />

                                    </ThemingFormGroupWrapper>
                                    {validationErrors.customMediaURLError}
                                </>
                            )}
                            <ThemingFormGroupWrapper
                                inheritedThemingPropertyValue={defaultStepContent.nativeCameraButtonText}
                                currentThemingPropertyValue={stepContent.nativeCameraButtonText}
                                pageLevel={pageLevel}
                            >
                                <InputFormGroup
                                    controlId={`dcui-theming-step-${stepType}-capture-button-label-input`}
                                    automationId={`$dcui-theming-step-${stepType}-capture-button-label-input`}
                                    disabled={isDisabled}
                                    isRequired={true}
                                    label={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.common.inputs.captureButtonLabel.label}
                                    ariaLabel={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.common.inputs.captureButtonLabel.label}
                                    labelWidth={labelWidth}
                                    layout={layout}
                                    onChange={handleCaptureButtonLabelChange}
                                    value={stepContent.nativeCameraButtonText}
                                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.common.inputs.captureButtonLabel.tooltip(stepResources.title)}
                                    afterLabelIcon={isDisabled ? null : faUndo}
                                    afterLabelIconOnClick={handleResetCaptureButtonLabel}
                                />
                            </ThemingFormGroupWrapper>
                            {validationErrors.captureButtonLabelError}
                        </ThemingFormSection>
                    </ThemingFormContainer>
                    <DCUIThemingPreviewContainer
                        currentTab={tabType}
                        dcuiContent={dcuiContent}
                        dcuiTheme={dcuiTheme}
                        stepType={stepType}
                        documentCaptureSteps={documentCaptureSteps}
                        previewEntityType={previewEntityType}
                    />
                </ThemingConfigurationContainer>
            </ContentPageBody>
        </div>
    );
};